import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AnalyticsFilterBar } from '@/components/analytics';
import { useVendors, useTeams, useCanvasserStats } from '@/hooks/queries';
import type { CanvasserAnalyticsFilters } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';
import { Coachmark } from '@/components/onboarding';
import { FloatingHelp, HELP_TOPICS } from '@/components/help';

const analyticsSearchSchema = z.object({
  type: z.enum(['Volunteer', 'Paid']).optional(),
  vendorId: z.string().optional(),
  teamId: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  campaignId: z.string().optional(),
});

export const Route = createFileRoute('/app/analytics')({
  validateSearch: analyticsSearchSchema,
  component: AnalyticsLayout,
});

function AnalyticsLayout() {
  const filters = Route.useSearch() as CanvasserAnalyticsFilters;
  const navigate = useNavigate();
  const { data: vendors } = useVendors();
  const { data: teams } = useTeams();
  // TanStack Query deduplicates with the same call in AnalyticsDashboard — no extra fetch
  const { dataUpdatedAt } = useCanvasserStats(filters);

  // Just-in-time spotlight on the performance header the first time a manager opens Analytics for
  // this campaign. Marked seen on show (once per campaign), mirroring the Staff/turf convention.
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id);
  const headerRef = useRef<HTMLDivElement>(null);
  const [showCoachmark, setShowCoachmark] = useState(false);
  const coachmarkStartedRef = useRef(false);

  useEffect(() => {
    if (coachmarkStartedRef.current || !campaignId) return;
    const seenKey = `sv:analytics-demo-seen:${campaignId}`;
    let seen = false;
    try {
      seen = localStorage.getItem(seenKey) === '1';
    } catch {
      /* ignore */
    }
    if (!seen) {
      coachmarkStartedRef.current = true;
      setShowCoachmark(true);
      try {
        localStorage.setItem(seenKey, '1');
      } catch {
        /* ignore */
      }
    }
  }, [campaignId]);

  const handleFiltersChange = (newFilters: CanvasserAnalyticsFilters) => {
    navigate({
      from: Route.fullPath,
      search: {
        type: newFilters.type,
        vendorId: newFilters.vendorId,
        teamId: newFilters.teamId,
        dateFrom: newFilters.dateFrom,
        dateTo: newFilters.dateTo,
      },
    });
  };

  return (
    <div className="w-full space-y-6">
      <div className="glass-surface rounded-lg border shadow-card p-4">
        <h1 className="text-2xl font-bold tracking-tight mb-4">Campaign Performance</h1>

        {/* Content-width wrapper so the first-visit spotlight hugs the filters and ends at the
            date filter, instead of spanning the full-width header. Caps at full width so it still
            wraps gracefully on narrow screens. */}
        <div ref={headerRef} className="w-fit max-w-full">
          <AnalyticsFilterBar
            filters={filters}
            onFiltersChange={handleFiltersChange}
            teams={(teams ?? []).map((t) => ({ id: t.id, name: t.name }))}
            vendors={(vendors ?? []).map((v) => ({ id: v.id, name: v.name }))}
            dataUpdatedAt={dataUpdatedAt}
          />
        </div>
      </div>

      <Outlet />

      <Coachmark
        active={showCoachmark}
        targetRef={headerRef}
        message="Track your campaign's performance here — contacts, doors knocked, and team outcomes. Use the filters to focus by canvasser type, team, vendor, or date range."
        onDismiss={() => setShowCoachmark(false)}
      />

      <FloatingHelp topic={HELP_TOPICS.analytics} />
    </div>
  );
}
